import React from 'react';

interface PipeclayTriangleProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
}

/**
 * Pipeclay triangle — ceramic-coated wire frame in a triangular shape used
 * on top of a tripod to support a crucible during strong heating. The
 * ceramic sleeves protect the wire from melting and provide a stable seat
 * for the crucible's round bottom.
 */
export function PipeclayTriangle({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
}: PipeclayTriangleProps) {
  const sideLength = 0.55;
  const wireRadius = 0.012;
  const ceramicRadius = 0.022;
  const ceramicLength = sideLength * 0.7;

  const apexes: [number, number, number][] = [
    [0, 0, -sideLength / Math.sqrt(3)],
    [sideLength / 2, 0, sideLength / (2 * Math.sqrt(3))],
    [-sideLength / 2, 0, sideLength / (2 * Math.sqrt(3))],
  ];

  const sides: { from: [number, number, number]; to: [number, number, number] }[] = [
    { from: apexes[0], to: apexes[1] },
    { from: apexes[1], to: apexes[2] },
    { from: apexes[2], to: apexes[0] },
  ];

  return (
    <group position={position} rotation={rotation} scale={scale}>
      {sides.map((side, i) => {
        const mx = (side.from[0] + side.to[0]) / 2;
        const my = (side.from[1] + side.to[1]) / 2;
        const mz = (side.from[2] + side.to[2]) / 2;
        const dx = side.to[0] - side.from[0];
        const dz = side.to[2] - side.from[2];
        const length = Math.hypot(dx, dz);
        const angleY = Math.atan2(dx, dz);

        return (
          <group key={i} position={[mx, my, mz]} rotation={[0, angleY, 0]}>
            <mesh rotation={[Math.PI / 2, 0, 0]} castShadow>
              <cylinderGeometry args={[wireRadius, wireRadius, length, 12]} />
              <meshStandardMaterial color="#3a3a3a" metalness={0.85} roughness={0.4} />
            </mesh>
            <mesh rotation={[Math.PI / 2, 0, 0]} castShadow>
              <cylinderGeometry args={[ceramicRadius, ceramicRadius, ceramicLength, 18]} />
              <meshStandardMaterial color="#e6dcc8" roughness={0.92} metalness={0.02} />
            </mesh>
          </group>
        );
      })}

      {apexes.map((a, i) => (
        <mesh key={i} position={a} castShadow>
          <sphereGeometry args={[wireRadius * 1.4, 12, 12]} />
          <meshStandardMaterial color="#2c2c2c" metalness={0.9} roughness={0.35} />
        </mesh>
      ))}
    </group>
  );
}

export default PipeclayTriangle;
